'use client';

import {useEffect, useRef, useState} from 'react';
import {useIsomorphicLayoutEffect} from 'usehooks-ts';

export interface UseIdleTimeoutOptions {
    /** Whether the idle timeout is enabled */
    enabled: boolean;
    /** Time in ms before showing the idle notification (default: 60000) */
    idleTimeout?: number;
    /** Time in ms the notification is visible before auto-timeout (default: 15000) */
    notificationTimeout?: number;
    /** Callback when the timeout expires */
    onTimeout: () => void;
}

export interface UseIdleTimeoutResult {
    isIdleNotificationVisible: boolean;
}

export function useIdleTimeout({
    enabled,
    idleTimeout = 60000,
    notificationTimeout = 15000,
    onTimeout,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
    const [isIdleNotificationVisible, setIsIdleNotificationVisible] = useState(false);
    const onTimeoutRef = useRef(onTimeout);
    const idleTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const notificationTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Remember the latest callback if it changes
    useIsomorphicLayoutEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    useEffect(() => {
        if (enabled) {
            startIdleTimeout();
        } else {
            clearAllTimeouts();
            setIsIdleNotificationVisible(false);
        }

        function clearAllTimeouts() {
            if (idleTimeoutIdRef.current != null) {
                clearTimeout(idleTimeoutIdRef.current);
                idleTimeoutIdRef.current = null;
            }
            if (notificationTimeoutIdRef.current != null) {
                clearTimeout(notificationTimeoutIdRef.current);
                notificationTimeoutIdRef.current = null;
            }
        }

        function startIdleTimeout() {
            clearAllTimeouts();
            setIsIdleNotificationVisible(false);

            idleTimeoutIdRef.current = setTimeout(() => {
                setIsIdleNotificationVisible(true);

                notificationTimeoutIdRef.current = setTimeout(() => {
                    onTimeoutRef.current();
                }, notificationTimeout);
            }, idleTimeout);
        }

        // Restart timeout on user activity
        function handleUserActivity() {
            if (!enabled) return;
            startIdleTimeout();
        }

        window.addEventListener('click', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('touchstart', handleUserActivity);

        return () => {
            window.removeEventListener('click', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('touchstart', handleUserActivity);
            clearAllTimeouts();
        };
    }, [enabled, idleTimeout, notificationTimeout]);

    return {
        isIdleNotificationVisible
    };
}
